<template>
  <div class="am-page">
    <div class="am-card">
      <h1 class="am-title">账号商城</h1>

      <div class="am-field">
        <label class="am-label" for="category">选择类目</label>
        <select
          id="category"
          v-model="category"
          class="am-select"
          @change="onCategoryChange"
        >
          <option
            v-for="cat in categories"
            :key="cat.value"
            :value="cat.value"
          >
            {{ cat.label }}
          </option>
        </select>
      </div>

      <div v-if="category" class="am-field">
        <label class="am-label">选择商品</label>
        <div class="am-product-grid">
          <button
            v-for="item in currentProducts"
            :key="item.id"
            type="button"
            class="am-product"
            :class="{ 'am-product--active': selectedProductId === item.id }"
            @click="selectedProductId = item.id"
          >
            <span class="am-product-name">{{ item.name }}</span>
            <span class="am-product-price">{{ item.price }}</span>
          </button>
        </div>
      </div>

      <p class="am-tip">支付完成后将在 5 分钟内生效</p>
      <p class="am-tip">
        若有问题请
        <a href="javascript:void(0)" class="am-link" @click.prevent="onContact">联系客服</a>
      </p>

      <button type="button" class="am-next" :disabled="loading" @click="onNext">
        {{ loading ? '提交中...' : '下一步' }}
      </button>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { addOrder } from '../util/api';
import { resolveReferralContext } from '../util/deeplink';
import { makeOrderNo, pushSelectWallet } from '../util/paymentFlow';

const CATEGORIES = [
  { value: '', label: '请选择类目' },
  { value: 'kuaishou', label: '快手账号' },
  { value: 'douyin', label: '抖音账号' },
  { value: 'wechat', label: '微信号' },
  { value: 'telegram', label: '飞机号' },
  { value: 'simcard', label: '手机卡' },
  { value: 'smsreceive', label: '接码' },
  { value: 'giftcard', label: '京东E卡/油卡' },
  { value: 'coins', label: '快币/抖币' },
];

const PRODUCT_MAP = {
  kuaishou: [
    { id: 'ks_full', name: '满月号', price: '2U' },
    { id: 'ks_half', name: '半年号', price: '5U' },
    { id: 'ks_year', name: '一年号', price: '10U' },
    { id: 'ks_1k', name: '千粉号', price: '12U' },
    { id: 'ks_10k', name: '万粉号', price: '80U' },
  ],
  douyin: [
    { id: 'dy_full', name: '满月号', price: '3U' },
    { id: 'dy_half', name: '半年号', price: '6U' },
    { id: 'dy_year', name: '一年号', price: '12U' },
    { id: 'dy_1k', name: '千粉号', price: '15U' },
    { id: 'dy_10k', name: '万粉号', price: '90U' },
  ],
  wechat: [
    { id: 'wc_full', name: '满月号', price: '8U' },
    { id: 'wc_half', name: '半年号', price: '20U' },
    { id: 'wc_year', name: '一年号', price: '38U' },
    { id: 'wc_2y', name: '两年号', price: '80U' },
    { id: 'wc_3y', name: '三年号', price: '120U' },
  ],
  telegram: [
    { id: 'tg_new', name: '新号', price: '3U' },
    { id: 'tg_old', name: '老号', price: '6U' },
    { id: 'tg_vip', name: '会员号', price: '8U' },
    { id: 'tg_888', name: '+888靓号', price: '1200U' },
    { id: 'tg_rent', name: '+888租赁', price: '40U' },
  ],
  simcard: [
    { id: 'sim_cmcc', name: '移动', price: '25U' },
    { id: 'sim_unicom', name: '联通', price: '22U' },
    { id: 'sim_telecom', name: '电信', price: '25U' },
    { id: 'sim_gd', name: '广电', price: '20U' },
    { id: 'sim_hk', name: '港卡', price: '12U' },
    { id: 'sim_my', name: '马来', price: '30U' },
  ],
  smsreceive: [
    { id: 'sms_national', name: '全国接码', price: '2U' },
  ],
  giftcard: [
    { id: 'gc_100', name: '100面值', price: '10.3U' },
    { id: 'gc_200', name: '200面值', price: '20.5U' },
    { id: 'gc_500', name: '500面值', price: '51U' },
    { id: 'gc_1000', name: '1000面值', price: '100U' },
    { id: 'gc_5000', name: '5000面值', price: '490U' },
  ],
  coins: [
    { id: 'coin_1k', name: '1k币', price: '10U' },
    { id: 'coin_1w', name: '1w币', price: '99U' },
    { id: 'coin_2w', name: '2w币', price: '190U' },
    { id: 'coin_5w', name: '5w币', price: '475U' },
    { id: 'coin_10w', name: '10w币', price: '900U' },
  ],
};

function parsePrice(text) {
  const n = parseFloat(String(text).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export default {
  name: 'AccountMall',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const category = ref('douyin');
    const selectedProductId = ref('');
    const loading = ref(false);
    const referral_code = ref('');
    const gameGroupId = ref('');

    const categories = CATEGORIES;

    const currentProducts = computed(() => {
      if (!category.value) return [];
      return PRODUCT_MAP[category.value] || [];
    });

    const selectedCategoryLabel = computed(() => {
      const hit = categories.find((c) => c.value === category.value);
      return hit && hit.value ? hit.label : '';
    });

    onMounted(() => {
      const ctx = resolveReferralContext(route);
      referral_code.value = ctx.referralCode;
      gameGroupId.value = ctx.gameGroupId;
    });

    const onCategoryChange = () => {
      selectedProductId.value = '';
    };

    const onContact = () => message.info('请联系客服');

    const onNext = async () => {
      if (!category.value) {
        message.warning('请选择类目');
        return;
      }
      const product = currentProducts.value.find((p) => p.id === selectedProductId.value);
      if (!product) {
        message.warning('请选择商品');
        return;
      }

      const catLabel = selectedCategoryLabel.value;
      const data = {
        price: parsePrice(product.price),
        inputNumber: 1,
        inputGT: '',
        orderNo: makeOrderNo(),
        title: `账号商城-${catLabel}-${product.name}`,
        referral_code: referral_code.value,
      };

      loading.value = true;
      try {
        await addOrder(data);
        pushSelectWallet(router, data, gameGroupId.value);
      } catch (e) {
        console.error(e);
        message.error('下单失败，请稍后重试');
      } finally {
        loading.value = false;
      }
    };

    return {
      category,
      categories,
      selectedProductId,
      selectedCategoryLabel,
      currentProducts,
      loading,
      onCategoryChange,
      onContact,
      onNext,
    };
  },
};
</script>

<style scoped>
.am-page {
  min-height: 100vh;
  min-height: 100dvh;
  background: #f0f7f0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom));
  box-sizing: border-box;
}

.am-card {
  width: 85%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  padding: 25px;
  box-sizing: border-box;
}

.am-title {
  margin: 0 0 20px;
  text-align: center;
  font-size: 28px;
  font-weight: 700;
  color: #2e8b57;
}

.am-field {
  margin-bottom: 20px;
}

.am-label {
  display: block;
  margin-bottom: 8px;
  font-size: 18px;
  color: #3a5a40;
}

.am-select {
  width: 100%;
  height: 45px;
  padding: 10px 36px 10px 10px;
  font-size: 16px;
  color: #333333;
  background-color: #f8fff8;
  border: 1px solid #a3c9a8;
  border-radius: 8px;
  box-sizing: border-box;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%233a5a40' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
  transition: border-color 0.3s ease;
}

.am-select:focus {
  border-color: #4caf50;
  outline: none;
}

.am-product-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: space-between;
}

.am-product {
  width: calc(33.33% - 10px);
  min-height: 64px;
  padding: 12px 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: #e8f5e9;
  border: 2px solid #a3c9a8;
  border-radius: 8px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.am-product--active {
  background: #c8e6c9;
  border-color: #4caf50;
}

.am-product-name {
  font-size: 15px;
  color: #333333;
}

.am-product-price {
  display: block;
  font-size: 13px;
  color: #5a8c5a;
}

.am-tip {
  margin: 0 0 6px;
  text-align: center;
  font-size: 16px;
  color: #444444;
  line-height: 1.6;
}

.am-link {
  color: #2e8b57;
  text-decoration: none;
  font-weight: 700;
}

.am-next {
  width: 100%;
  margin-top: 30px;
  padding: 15px;
  border: none;
  border-radius: 8px;
  background: #4caf50;
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.3s ease;
}

.am-next:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.am-next:active:not(:disabled) {
  background: #388e3c;
}

@media (max-width: 480px) {
  .am-page {
    align-items: flex-start;
    padding-top: 20px;
  }

  .am-card {
    width: 85%;
    max-width: none;
    padding: 22px 14px 26px;
  }

  .am-product-grid {
    gap: 10px;
  }

  .am-product {
    width: calc(33.33% - 7px);
    min-height: 58px;
    padding: 10px 4px;
  }

  .am-product-name {
    font-size: 13px;
  }

  .am-product-price {
    font-size: 12px;
  }
}
</style>
